import { ControlledFormInput } from '@components/formInputs/ControlledFormInput';
import { ControlledFormSelect } from '@components/formInputs/ControlledFormSelect';
import { ControlledFormTextareaAutosize } from '@components/formInputs/ControlledFormTextareaAutosize';
import { FormTooltip } from '@components/formInputs/FormTooltip';
import { LayoutVariant } from '@components/formInputs/sharedTypes';
import { ValidationErrorMessage } from '@components/formInputs/ValidationErrorMessage';
import { ImageUploader } from '@shared/components/imageUploader/ImageUploader';
import { errorToast } from '@shared/components/toasts/Toasts';
import { getRestaurantImagePath } from '@shared/utils/getRestaurantImagePath';
import { reportAppError } from '@shared/reportAppError';
import { useQuery } from '@tanstack/react-query';
import cx from 'classnames';
import { useEffect } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import typography from '~styles/typography.scss';
import { SettingsOutletHeader } from '../../shared/SettingsOutletHeader';
import {
  SHOW_HIDE_SELECT_OPTIONS,
  YES_NO_SELECT_OPTIONS,
} from '../utils/generalSettingsFormHelpers';
import { type GeneralSettingsForm } from '../utils/generalSettingsFormTypes';
import {
  getRestaurantHeroImageUploadUrl,
  getRestaurantHeroImages,
  restaurantHeroImagesQueryKey,
} from './apiHelpers';
import { getRestaurantHeroImageUploadContentType } from './restaurantHeroImageUpload';
import styles from './RestaurantConfigurationSection.scss';

const MAX_IMAGE_SIZE_MB = 5;
const MAX_DESCRIPTION_LENGTH = 600;
const MAX_HERO_IMAGES = 20;
const MIN_HERO_IMAGES = 1;
const HERO_IMAGES_REQUIRED_MESSAGE = 'At least one photo is required';

const DRESS_CODE_REQUIRED_EXPLANATION =
  'Guests must accept the dress code at checkout before they can reserve.';

const DRESS_CODE_OPTIONAL_EXPLANATION =
  'Guests see the dress code as information only and can reserve without accepting it.';

const RESTAURANT_CONFIGURATION_EXPLANATION =
  'Configure how your restaurant appears on the guest landing page, including description, dining style, dress code, and hero photos.';

export interface RestaurantConfigurationSectionProps {
  restaurantId: string;
  sectionTitleId: string;
}

export const RestaurantConfigurationSection = ({
  restaurantId,
  sectionTitleId,
}: RestaurantConfigurationSectionProps) => {
  const { control, setValue, getValues, formState, resetField } =
    useFormContext<GeneralSettingsForm>();
  const {
    field: { value: heroImageKeysValue },
    fieldState: { error: heroImageKeysError },
  } = useController({
    control,
    defaultValue: [],
    name: 'heroImageKeys',
    rules: {
      validate: (value) =>
        (value?.length ?? 0) >= MIN_HERO_IMAGES || HERO_IMAGES_REQUIRED_MESSAGE,
    },
  });
  const queryKey = restaurantHeroImagesQueryKey(restaurantId);
  const isHeroImageKeysDirty = !!formState.dirtyFields.heroImageKeys;

  const dressCode = useWatch({ control, name: 'dressCode' });
  const dressCodeRequired = useWatch({ control, name: 'dressCodeRequired' });
  const hasDressCode = (dressCode ?? '').trim() !== '';

  // Clearing the dress code removes the field below, so drop the requirement
  // with it rather than leaving a hidden Yes to be saved.
  useEffect(() => {
    if (hasDressCode || dressCodeRequired === 0) {
      return;
    }
    setValue('dressCodeRequired', 0, { shouldDirty: true });
  }, [dressCodeRequired, hasDressCode, setValue]);

  const {
    data,
    isPending: isLoadingHeroImages,
    isError,
    error,
  } = useQuery({
    enabled: !!restaurantId,
    queryFn: () => getRestaurantHeroImages(restaurantId),
    queryKey,
  });

  // Keep the form aligned with the query while the field is clean. Use
  // resetField so loaded keys become the dirty baseline — otherwise clearing
  // back to [] matches the initial default and the sync restores removed photos.
  // After save, GeneralPage updates this cache before auth refresh remounts.
  useEffect(() => {
    if (!data || isHeroImageKeysDirty) {
      return;
    }
    resetField('heroImageKeys', { defaultValue: data.imageKeys });
  }, [data, isHeroImageKeysDirty, resetField]);

  useEffect(() => {
    if (!isError) {
      return;
    }
    reportAppError(error);
    errorToast({ message: 'Unable to load hero photos' });
  }, [error, isError]);

  const heroImageKeys = heroImageKeysValue ?? [];
  const heroImagePreviewUrls = heroImageKeys
    .map((imageKey) => getRestaurantImagePath(imageKey))
    .filter((url) => url !== '');

  const getUploadUrl = async (file?: File) => {
    if (file == null) {
      throw new Error('Missing file for hero image upload');
    }
    const contentType = getRestaurantHeroImageUploadContentType(file);
    const res = await getRestaurantHeroImageUploadUrl(
      restaurantId,
      contentType,
    );
    return {
      signedUploadUrl: res.signedUploadImageUrl,
      objectKey: res.objectKey,
      uploadContentType: res.contentType,
    };
  };

  const handleImageUpload = (objectKey: string) => {
    const nextKeys = [...heroImageKeys, objectKey];
    setValue('heroImageKeys', nextKeys, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleRemoveImagesAt = (indices: number[]) => {
    if (indices.length === 0) {
      return;
    }

    const indicesToRemove = new Set(indices);
    const nextKeys = heroImageKeys.filter(
      (_, index) => !indicesToRemove.has(index),
    );
    setValue('heroImageKeys', nextKeys, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleReorderImages = (fromIndex: number, toIndex: number) => {
    const nextKeys = [...(getValues('heroImageKeys') ?? [])];
    const [moved] = nextKeys.splice(fromIndex, 1);
    if (moved == null) {
      return;
    }
    nextKeys.splice(toIndex, 0, moved);
    setValue('heroImageKeys', nextKeys, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <section className={styles.section} aria-labelledby={sectionTitleId}>
      <SettingsOutletHeader
        variant="section"
        titleId={sectionTitleId}
        title="Restaurant Details"
        subtitle={RESTAURANT_CONFIGURATION_EXPLANATION}
      />
      <div className={styles.formFields}>
        <div>
          <ControlledFormSelect
            control={control}
            label="Google Rating"
            name="showGoogleRating"
            options={SHOW_HIDE_SELECT_OPTIONS}
            rules={{ required: true }}
            variant={LayoutVariant.Vertical}
            className={styles.constrainedField}
          />
        </div>
        <ControlledFormTextareaAutosize
          control={control}
          label="Description"
          name="description"
          placeholder="Describe your restaurant..."
          minRows={3}
          maxRows={8}
          rules={{
            maxLength: {
              value: MAX_DESCRIPTION_LENGTH,
              message: `Must be ${MAX_DESCRIPTION_LENGTH} characters or fewer`,
            },
          }}
        />
        <div className={styles.containerWithTwoFields}>
          <div>
            <ControlledFormInput
              control={control}
              label="Dining Type"
              name="dining"
              placeholder="e.g. Fine Dining"
              type="text"
              variant={LayoutVariant.Vertical}
            />
          </div>
          <div>
            <ControlledFormInput
              control={control}
              label="Dress Code"
              name="dressCode"
              placeholder="e.g. Smart Casual"
              type="text"
              variant={LayoutVariant.Vertical}
            />
          </div>
        </div>
        <ControlledFormTextareaAutosize
          control={control}
          label="Dress Code Detail"
          name="dressCodeDescription"
          placeholder="Share dress code details guests should know..."
          minRows={3}
          maxRows={8}
        />
        {hasDressCode && (
          <div>
            <ControlledFormSelect
              control={control}
              label="Require guests to acknowledge"
              name="dressCodeRequired"
              options={YES_NO_SELECT_OPTIONS}
              rules={{ required: true }}
              variant={LayoutVariant.Vertical}
              className={styles.constrainedField}
            />
            <p className={cx(typography.c3, styles.helperText)}>
              {dressCodeRequired === 1
                ? DRESS_CODE_REQUIRED_EXPLANATION
                : DRESS_CODE_OPTIONAL_EXPLANATION}
            </p>
          </div>
        )}
        <div className={styles.photosSection}>
          <div className={styles.photosLabelRow}>
            <span className={cx(typography.c3, styles.sectionLabel)}>
              Photos{' '}
              <span className={styles.sectionLabelRequired}>(required)</span>
            </span>
            <FormTooltip
              label="restaurantHeroImages"
              text="The first image in the list will be the first image guests see in the landing page carousel"
            />
          </div>
          {!isLoadingHeroImages && (
            <ImageUploader
              variant="imageGrid"
              imageUrl={null}
              imageUrls={heroImagePreviewUrls}
              getUploadUrl={getUploadUrl}
              onRemoveImagesAt={handleRemoveImagesAt}
              onReorderImages={handleReorderImages}
              onImageUpload={handleImageUpload}
              multiple
              maxSelectableImages={MAX_HERO_IMAGES}
              maxSizeMB={MAX_IMAGE_SIZE_MB}
              slotRowTestId="restaurant-hero-image-slots"
              uploadInputTestId="restaurant-hero-image-input"
            />
          )}
          <ValidationErrorMessage<GeneralSettingsForm>
            error={heroImageKeysError}
            label="Photos"
            name="heroImageKeys"
          />
          <p className={cx(typography.c3, styles.helperText)}>
            Recommended: 3:4 images with the subject centered (min ~2000px on
            the short side). JPEG, PNG, WebP, or GIF up to {MAX_IMAGE_SIZE_MB}
            MB.
          </p>
        </div>
      </div>
    </section>
  );
};
